"use client";

import React, { useCallback, useMemo, useState } from "react";
import { Download, Loader2, Paperclip } from "lucide-react";
import { useAuth } from "@west-tennessee-consulting/ui";

import { Button } from "@/components/ui/button";
import { formatFileSize } from "@/lib/utils/file-utils";

import { isListableAttachment } from "./attachment-visibility";
import type { SupportInboxAttachment } from "./types";

interface SupportInboxAttachmentsProps {
  companyId: string;
  threadId: string;
  messageId: string;
  attachments: SupportInboxAttachment[] | undefined;
}

/**
 * The attachments on one support-inbox message, with per-file download.
 *
 * Renders for BOTH directions of the conversation: inbound customer mail and the
 * BCC'd copy of our own outbound reply (replies BCC the group address, so our
 * sent copy lands back in the same thread). That second case is the point — it
 * is how an operator confirms a document actually went out.
 */
export function SupportInboxAttachments({
  companyId,
  threadId,
  messageId,
  attachments,
}: SupportInboxAttachmentsProps) {
  const { getAuthToken } = useAuth();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const visible = useMemo(
    () => (attachments ?? []).filter(isListableAttachment),
    [attachments],
  );

  const handleDownload = useCallback(
    async (attachment: SupportInboxAttachment) => {
      setDownloadingId(attachment.attachmentId);
      setError(null);
      let objectUrl: string | null = null;
      try {
        const token = await getAuthToken();
        const url =
          `/api/companies/${encodeURIComponent(companyId)}` +
          `/support-inbox/thread/${encodeURIComponent(threadId)}/attachment` +
          `?messageId=${encodeURIComponent(messageId)}` +
          `&attachmentId=${encodeURIComponent(attachment.attachmentId)}`;

        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          cache: "no-store",
        });

        if (!response.ok) {
          let message = `Download failed (${response.status})`;
          try {
            const body = (await response.json()) as { error?: string };
            if (body?.error) message = body.error;
          } catch {
            // non-JSON error body — keep the status message
          }
          throw new Error(message);
        }

        const blob = await response.blob();
        objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = attachment.filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "We couldn't download that file.",
        );
      } finally {
        if (objectUrl) window.URL.revokeObjectURL(objectUrl);
        setDownloadingId(null);
      }
    },
    [companyId, threadId, messageId, getAuthToken],
  );

  if (visible.length === 0) return null;

  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <Paperclip className="h-3.5 w-3.5" aria-hidden />
        {visible.length === 1 ? "1 attachment" : `${visible.length} attachments`}
      </p>
      <ul className="mt-1.5 space-y-1">
        {visible.map((attachment) => {
          const isDownloading = downloadingId === attachment.attachmentId;
          return (
            <li
              key={attachment.attachmentId}
              className="flex items-center gap-2 text-sm"
            >
              <span
                className="min-w-0 flex-1 truncate text-card-foreground"
                title={attachment.filename}
              >
                {attachment.filename}
              </span>
              {attachment.sizeBytes > 0 && (
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {formatFileSize(attachment.sizeBytes)}
                </span>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2"
                onClick={() => void handleDownload(attachment)}
                disabled={isDownloading}
                title={`Download ${attachment.filename}`}
                aria-label={`Download ${attachment.filename}`}
              >
                {isDownloading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
              </Button>
            </li>
          );
        })}
      </ul>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
